import * as Yup from 'yup';
import {
  addressRegExp,
  citiesRegExp,
  doorRegExp,
  floorRegExp,
  numberAddressRegExp,
  postalRegExp,
} from 'utils/regularExpressions';
import {
  ALPHANUMERIC_WITH_SPACES_PATTERN,
  JUICY_NEXT_BTN,
} from 'utils/constants';
import { AppRegistrationSection } from 'AppRegistrationSection';
import { ButtonActor, InputActor, SelectActor } from 'components';
import { FormikConfig, FormikValues, useFormik } from 'formik';
import { FormTemplateProps } from 'types/components/FormViewTemplate';
import { Frame } from 'components/organisms';
import { frameConfig } from 'AppConfig';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { getScreenTranslation } from 'utils/translations';
import { routes } from 'routes';
import { TestingContext } from 'InternalLibrary/testing';
import { useContext, useEffect, useState } from 'react';
import { useErrorManager } from 'InternalLibrary/app';
import { useGetClientApplication, usePutProfile } from 'api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMaybeCreateApplication } from 'hooks/useMaybeCreateApplication';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import CreditLimitWarningBox from 'components/CreditLimitWarningBox/CreditLimitWarningBox';
import FieldGroup from 'components/atoms/FieldGroup/FieldGroup';
import IdentifyEvent from '../../hooks/eventCollector/events/IdentifyEvent';
import layout from 'scss/layout.module.scss';
import provinces from 'localizations/provinces';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './AddressDetails.module.scss';
import Title from 'components/atoms/Title/Title';
import useClient from '../../hooks/useClient';
import useEventCollector from '../../hooks/eventCollector/useEventCollector';

export type AddressDetailsForm = {
  address: string;
  number: string;
  floor: string;
  door: string;
  portal: string;
  postalCode: string;
  province: string;
  city: string;
};

export default function AddressDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetch: fetchPutProfile } = usePutProfile();
  const { maybeCreateApplication } = useMaybeCreateApplication();
  const { client, fetchClient } = useClient();
  const { data: application } = useGetClientApplication();
  const [validationErrors] = useState(
    location.search.includes('age-error=true')
      ? {
          _submit: [<CreditLimitWarningBox />],
        }
      : {},
  );
  const { setError } = useErrorManager();
  const { identify } = useEventCollector();
  const { setTestingData } = useContext(TestingContext);
  const i18next = useTranslation();

  const validationSchema = Yup.object().shape({
    address: Yup.string()
      .matches(addressRegExp, i18next.t('validation:wrong_format'))
      .required(i18next.t('validation:required_field'))
      .min(2, i18next.t('validation:too_short')),
    number: Yup.string()
      .matches(
        numberAddressRegExp,
        i18next.t('validation:wrong_number_address_format'),
      )
      .max(15, i18next.t('validation:too_long'))
      .required(i18next.t('validation:required_direction_number')),
    floor: Yup.string()
      .matches(floorRegExp, i18next.t('validation:wrong_floor_address_format'))
      .max(4, i18next.t('validation:too_long')),
    door: Yup.string()
      .matches(doorRegExp, i18next.t('validation:wrong_door_address_format'))
      .max(3, i18next.t('validation:too_long')),
    portal: Yup.string(),
    postalCode: Yup.string()
      .required(i18next.t('validation:required_field'))
      .matches(postalRegExp, i18next.t('validation:wrong_format')),
    province: Yup.string().required(i18next.t('validation:required_field')),
    city: Yup.string()
      .matches(citiesRegExp, i18next.t('validation:wrong_format'))
      .required(i18next.t('validation:required_field')),
  });

  useEffect(() => {
    if (application?.id || client?.id) {
      setTestingData({
        applicationID: String(application?.id),
        clientID: String(client?.id),
      });
    }
  }, [application, client]);

  const onSubmit = async (data: any) => {
    try {
      const location3 = data.portal.length
        ? `${data.address} (${data.portal})`
        : data.address;
      await fetchPutProfile({
        headers: getFetchHeaders(),
        body: {
          address: {
            postalCode: data.postalCode,
            location1: data.province,
            location2: data.city,
            location3,
            location4: data.number,
            location5: data.floor,
            location6: data.door,
          },
          registrationPhase: 'ADDRESS',
        },
      });

      await fetchClient();

      if (client)
        identify(new IdentifyEvent({ client, email: client?.email?.email }));

      await maybeCreateApplication({
        nextRoute: routes.FirstLoan_Income,
      });

      navigate(routes.FirstLoan_Income);
    } catch (error) {
      setError(error);
    }
  };

  const content = getScreenTranslation('addressDetails', i18next);
  const { loading, formik } = useTemplateFormik<AddressDetailsForm>({
    initialValues: {
      address: '',
      number: '',
      floor: '',
      door: '',
      portal: '',
      postalCode: '',
      province: '',
      city: '',
    },
    validationSchema,
    errors: validationErrors,
    onSubmit,
  });

  function useTemplateFormik<TValues extends FormikValues>({
    initialValues,
    validationSchema,
    onSubmit,
    errors,
  }: Omit<FormTemplateProps<TValues>, 'initialValues'> & {
    initialValues: FormikConfig<TValues>['initialValues'];
  }) {
    const [loading, setLoading] = useState(false);
    const formik = useFormik<TValues>({
      initialValues,
      validationSchema,
      onSubmit: async (...args) => {
        formik.setStatus({ errors: {} });
        setLoading(true);
        await onSubmit(...args);
        setLoading(false);
      },
    });

    useEffect(() => {
      formik.setStatus({ errors });
      return () => {
        formik.setStatus({ errors: {} });
      };
    }, [errors]);

    return {
      loading,
      formik,
    };
  }

  return (
    <Frame {...frameConfig} sectionID={AppRegistrationSection.Confirmation}>
      <form onSubmit={formik.handleSubmit}>
        <div className={layout['width-sm2']}>
          <Spacer marginBottom="30px">
            <Title text={content.title} data-test="css-Address-Title" />
          </Spacer>

          <Spacer marginBottom="30px">
            <FieldGroup direction="vertical">
              <InputActor
                label={content.addressPlaceholder}
                name="address"
                formik={formik}
                data-test="id-Address"
                analytics={{
                  description: 'Street',
                }}
              />
              <FieldGroup direction="horizontal">
                <InputActor
                  className={styles['border-left']}
                  label={content.addressNumberPlaceholder}
                  name="number"
                  inputProps={{
                    className:
                      styles['address-details-fieldgroup-input-container'],
                    inputClassName: layout['full-width-border-box'],
                    pattern: ALPHANUMERIC_WITH_SPACES_PATTERN,
                  }}
                  formik={formik}
                  data-test="id-Number"
                  analytics={{
                    description: 'House number',
                  }}
                />
                <InputActor
                  className={styles.border}
                  label={content.addressFloorPlaceholder}
                  name="floor"
                  inputProps={{
                    className:
                      styles['address-details-fieldgroup-input-container'],
                    inputClassName: layout['full-width-border-box'],
                    allowSpaces: false,
                    pattern: ALPHANUMERIC_WITH_SPACES_PATTERN,
                  }}
                  formik={formik}
                  data-test="id-Floor"
                  analytics={{
                    description: 'Floor/flat number',
                  }}
                />
                <InputActor
                  className={styles['border-right']}
                  label={content.addressLetterPlaceholder}
                  name="door"
                  inputProps={{
                    className:
                      styles['address-details-fieldgroup-input-container'],
                    inputClassName: layout['full-width-border-box'],
                    allowSpaces: false,
                    pattern: ALPHANUMERIC_WITH_SPACES_PATTERN,
                  }}
                  formik={formik}
                  data-test="id-Letter"
                  analytics={{
                    description: 'Letter',
                  }}
                />
              </FieldGroup>
              <InputActor
                className={styles['border-right']}
                label={content.addressPortal}
                name="portal"
                inputProps={{
                  className:
                    styles['address-details-fieldgroup-input-container'],
                  inputClassName: layout['full-width-border-box'],
                  pattern: ALPHANUMERIC_WITH_SPACES_PATTERN,
                }}
                formik={formik}
                data-test="id-portal"
                analytics={{
                  description: 'portal',
                }}
              />
            </FieldGroup>
          </Spacer>

          <Spacer marginBottom={{ default: '60px', m: '50px' }}>
            <FieldGroup direction="vertical">
              <InputActor
                label={content.addressPostalCodePlaceholder}
                name="postalCode"
                formik={formik}
                data-test="postal-Code"
                analytics={{
                  description: 'Postal code',
                }}
                inputProps={{
                  allowSpaces: false,
                }}
              />
              <SelectActor
                label={content.addressProvincePlaceholder}
                selectProps={{
                  className: styles['safari-only'],
                  options: provinces,
                }}
                name="province"
                formik={formik}
                data-test="css-Province"
                analytics={{
                  description: 'Province (county)',
                }}
              />
              <InputActor
                className={styles['border-bottom']}
                label={content.addressCityPlaceholder}
                name="city"
                formik={formik}
                data-test="id-City"
                analytics={{
                  description: 'City',
                }}
              />
            </FieldGroup>
          </Spacer>

          <Spacer marginBottom={{ s: '40px' }}>
            <ButtonActor
              buttonProps={{
                className: classes(styles['continue-button'], JUICY_NEXT_BTN),
                type: 'submit',
                loading,
                dataTest: 'css-Continue-Button',
              }}
              formik={formik}
              analytics={{
                description: 'Continue button',
              }}
            >
              {content.continueButtonText}
            </ButtonActor>
          </Spacer>
        </div>
      </form>
    </Frame>
  );
}
